import { ResourceType } from '@/components/ResourcesFilterBar';
import {
  Text,
  Heading,
  Box,
  Flex,
  Image,
  Link as UiLink,
} from '@everlywell/ui-kit';
import Layout from 'components/Layout';
import PDFViewer from 'components/PDFViewer';
import { SEO } from 'components/SEO';
import ShareButtons from 'components/ShareButtons';
import { graphql, PageProps } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import track from 'utils/analytics/track';
import { ANALYTICS } from 'utils/constants/analytics';
import { formatDate } from 'utils/dateUtils';
import { BlogPostQueryResult } from 'utils/types';

import { ASSET_URLS } from '../constants/assetUrls';

export const Head = ({ data }: PageProps<BlogPostQueryResult>) => {
  const post = data?.contentfulBlogPost;

  if (!post) {
    return <SEO title="Blog Post Not Found" />;
  }

  return (
    <SEO
      title={post.title}
      description={post.headerContent?.headerContent || ''}
      url={typeof window !== 'undefined' ? window.location.href : ''}
      image={post.featuredImage?.url || ''}
    />
  );
};

const BlogPostTemplate: React.FC<PageProps<BlogPostQueryResult>> = ({
  data,
  location,
}) => {
  const post = data?.contentfulBlogPost;
  const currentUrl = typeof location !== 'undefined' ? location.href : '';
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (post) {
      track({
        event: ANALYTICS.EVENTS.VIEWED_EHS_PAGE,
        data: {
          label: post.title,
        },
      });
    }

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [post]);

  if (isLoading) return null;

  if (!post) {
    return (
      <Layout>
        <Box textAlign="center" padding={10} paddingTop="7rem">
          <Heading as="h1" size="lg">
            Resource not found
          </Heading>
          <UiLink href="/resources" marginTop={4} display="block">
            Return to Resources
          </UiLink>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        data-testid="blog-post"
        bgColor="#EFF9F5"
        paddingTop={['6rem', '6rem', '7rem']}
        paddingX="20px"
      >
        <Flex
          maxWidth="1440px"
          bgColor="#EFF9F5"
          width="100%"
          margin="0 auto"
          direction={['column', 'column', 'row']}
          justifyContent="space-between"
          alignItems={['flex-start', 'flex-start', 'center']}
          gap={4}
        >
          <Box maxWidth="600px">
            <Image
              src={
                getTypeBadgeInfo(getContentType(post.contentType))?.src || ''
              }
              alt={
                getTypeBadgeInfo(getContentType(post.contentType))?.alt || ''
              }
              width="83px"
              height="29px"
              marginBottom={4}
            />

            <Heading as="h1" size="lg" marginBottom={4}>
              {post.title}
            </Heading>

            <Text size="md" color="#545454">
              {post.audience && post.focus
                ? `${post.audience}, ${post.focus}`
                : ''}
            </Text>
          </Box>

          <Box>
            {post.featuredImage?.gatsbyImageData && (
              <Box
                position="relative"
                width="100%"
                maxWidth="480px"
                borderRadius="md"
                sx={{
                  aspectRatio: '4/3',
                  overflow: 'hidden',
                  '.gatsby-image-wrapper': {
                    height: '100%',
                  },
                }}
              >
                <GatsbyImage
                  image={
                    getImage(post.featuredImage) ||
                    post.featuredImage.gatsbyImageData
                  }
                  alt={
                    post.featuredImage?.description ||
                    post.featuredImage?.title ||
                    `Featured image for ${post.title}`
                  }
                  style={{
                    borderRadius: '12px 12px 0 0',
                    width: '100%',
                    height: '100%',
                  }}
                  imgStyle={{
                    objectFit: 'contain',
                    objectPosition: 'center bottom',
                  }}
                />
              </Box>
            )}
            {!post.featuredImage?.gatsbyImageData && (
              <Image
                src={post.featuredImage?.url || ASSET_URLS.DEFAULT_BLOG_IMAGE}
                alt={
                  post.featuredImage?.description ||
                  post.featuredImage?.title ||
                  `Featured image for ${post.title}`
                }
                width="100%"
                maxWidth="480px"
                height="auto"
              />
            )}
          </Box>
        </Flex>
      </Box>

      {/* Header Content */}
      <Flex
        justifyContent="space-between"
        direction={['column', 'column', 'row']}
        alignItems="flex-start"
        maxWidth="1440px"
        marginX="auto"
        marginY={['1.5rem', '1.5rem', '3rem']}
        paddingX={[4, 6, 6]}
        color="#545454"
        fontSize="14px"
        gap={[5, 5, 10]}
      >
        <Box maxWidth="750px" lineHeight={1.5} order={[2, 2, 1]}>
          {post.headerContent?.headerContent}
        </Box>
        <Flex alignItems="center" gap={2} order={[1, 1, 2]}>
          <Box>{formatDate(post.publishDate)}</Box>
          {currentUrl && (
            <ShareButtons
              url={currentUrl}
              title={post.title}
              iconSrc={ASSET_URLS.SHARE_ICON}
            />
          )}
        </Flex>
      </Flex>

      {/* PDF Attachment */}
      {post.pdfAttachment?.url && (
        <Box
          width="100%"
          maxWidth="1200px"
          margin="0 auto"
          paddingX={[2, 4, 4]}
          marginBottom={10}
          overflow="hidden"
          sx={{
            '@media print': {
              pageBreakInside: 'avoid',
            },
          }}
        >
          <PDFViewer
            url={post.pdfAttachment.url}
            title={post.pdfAttachment.title || `${post.title} PDF`}
          />
        </Box>
      )}
    </Layout>
  );
};

export default BlogPostTemplate;

const getTypeBadgeInfo = (type?: ResourceType | string) => {
  switch (type) {
    case '1 Pager':
      return {
        src: 'https://images.ctfassets.net/6huuyzvsw0nv/6XxH8RCXl8EdgTAu9Frumx/0962b108f3b454fe23836bce2b15212b/1-pager.svg',
        alt: 'One Pager',
      };
    case 'Whitepaper':
      return {
        src: 'https://images.ctfassets.net/6huuyzvsw0nv/37fdi3TwtwLRI0pa2eYhq3/6ccf82df3d0a1d46914df64f3b302fb5/whitepaper.svg',
        alt: 'Whitepaper',
      };
    case 'Case Study':
      return {
        src: 'https://images.ctfassets.net/6huuyzvsw0nv/2QuofExJxqq5Rq61TdVNWN/63a5320d6a7a940f94c50ba4d4355560/case-study.svg',
        alt: 'Case Study',
      };
    default:
      return null;
  }
};

const getContentType = (
  contentType?: string | string[],
): string | undefined => {
  if (Array.isArray(contentType) && contentType.length > 0) {
    return contentType[0];
  }
  return contentType as string | undefined;
};

export const query = graphql`
  query BlogPostBySlug($slug: String!) {
    contentfulBlogPost(slug: { eq: $slug }) {
      id
      title
      slug
      contentType
      audience
      focus
      publishDate
      featuredImage {
        url
        title
        description
        gatsbyImageData(
          width: 480
          placeholder: BLURRED
          formats: [AUTO, WEBP]
          layout: CONSTRAINED
        )
      }
      headerContent {
        headerContent
      }
      pdfAttachment {
        url
        title
        description
      }
    }
  }
`;
